// Demo story: organizations, outlet sites, recycler facilities, zones, collection points.

import {
  DEMO_OUTLET_NAME,
  DEMO_OUTLET_NAME_AR,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_NAME,
  DEMO_RECYCLER_NAME_AR,
  DEMO_RECYCLER_ORG_ID,
  ids,
} from '../constants';
import type { DemoPersona, DemoSeedOrganization } from '../demoMode';

export type DemoCity = 'Riyadh' | 'Jeddah';

export const DEMO_CITY_AR: Record<DemoCity, string> = { Riyadh: 'الرياض', Jeddah: 'جدة' };

// ── Organizations ──────────────────────────────────────────────────────────

export const DEMO_OUTLET_ORG: DemoSeedOrganization = {
  id: DEMO_OUTLET_ORG_ID,
  name: DEMO_OUTLET_NAME,
  name_ar: DEMO_OUTLET_NAME_AR,
  organization_type: 'waste_outlet',
  city: 'Riyadh',
  country: 'SA',
  email: 'sustainability@alnakheel.example',
  phone: '+966112000100',
  cr_number: '1010000101',
  vat_number: '300000000100003',
  logo_url: null,
  data: {},
};

export const DEMO_RECYCLER_ORG: DemoSeedOrganization = {
  id: DEMO_RECYCLER_ORG_ID,
  name: DEMO_RECYCLER_NAME,
  name_ar: DEMO_RECYCLER_NAME_AR,
  organization_type: 'recycler',
  city: 'Riyadh',
  country: 'SA',
  email: 'operations@riyadhgreen.example',
  phone: '+966112000200',
  cr_number: '1010000202',
  vat_number: '300000000200003',
  logo_url: null,
  data: {},
};

/** A fresh copy of the org a persona starts as (what DemoEntry seeds as selectedOrganization). */
export function demoOrgForPersona(persona: DemoPersona): DemoSeedOrganization {
  const org = persona === 'outlet' ? DEMO_OUTLET_ORG : DEMO_RECYCLER_ORG;
  return { ...org, data: { ...(org.data ?? {}) } };
}

// ── Outlet sites ───────────────────────────────────────────────────────────

export interface DemoSite {
  id: string;
  code: string;
  name_en: string;
  name_ar: string;
  city: DemoCity;
  city_ar: string;
  lat: number;
  lng: number;
  address: string;
  site_type: 'branch' | 'distribution_center';
  opening_hours: string;
  manager_name: string;
  phone: string;
}

type SiteRow = [string, string, string, DemoCity, number, number, string, DemoSite['site_type'], string, string];

const SITE_ROWS: SiteRow[] = [
  ['RUH-OLY', 'Olaya Flagship', 'فرع العليا الرئيسي', 'Riyadh', 24.7005, 46.68, 'Olaya St, Al Olaya, Riyadh 12214', 'branch', '07:00-24:00', 'Khalid Al-Otaibi'],
  ['RUH-MLQ', 'Al Malqa', 'فرع الملقا', 'Riyadh', 24.8071, 46.6128, 'Anas Bin Malik Rd, Al Malqa, Riyadh 13521', 'branch', '07:00-23:00', 'Sara Al-Dosari'],
  ['RUH-NSM', 'Al Nasim', 'فرع النسيم', 'Riyadh', 24.736, 46.825, 'Hassan Bin Thabit St, Al Nasim, Riyadh 14241', 'branch', '07:00-23:00', 'Abdullah Al-Shehri'],
  ['RUH-DC10', 'Exit 10 Distribution Centre', 'مركز التوزيع - مخرج 10', 'Riyadh', 24.7665, 46.772, 'Eastern Ring Rd, Exit 10, Al Rawabi, Riyadh 14214', 'distribution_center', '24 hours', 'Majed Al-Mutairi'],
  ['JED-RWD', 'Al Rawdah', 'فرع الروضة', 'Jeddah', 21.558, 39.159, 'Prince Sultan Rd, Al Rawdah, Jeddah 23434', 'branch', '07:00-24:00', 'Hind Al-Ghamdi'],
  ['JED-SLM', 'Al Salamah', 'فرع السلامة', 'Jeddah', 21.59, 39.155, 'Sari St, Al Salamah, Jeddah 23525', 'branch', '07:00-23:00', 'Yousef Al-Malki'],
];

export const DEMO_SITES: DemoSite[] = SITE_ROWS.map(
  ([code, name_en, name_ar, city, lat, lng, address, site_type, opening_hours, manager_name], i) => ({
    id: ids.site(i + 1),
    code,
    name_en,
    name_ar,
    city,
    city_ar: DEMO_CITY_AR[city],
    lat,
    lng,
    address,
    site_type,
    opening_hours,
    manager_name,
    phone: `${city === 'Jeddah' ? '+96612' : '+96611'}200${String(1011 + i)}`,
  }),
);

export function siteById(id: string): DemoSite | undefined {
  return DEMO_SITES.find((s) => s.id === id);
}

// ── Recycler facilities ────────────────────────────────────────────────────

export interface DemoFacility {
  id: string;
  code: string;
  name_en: string;
  name_ar: string;
  city: DemoCity;
  city_ar: string;
  lat: number;
  lng: number;
  address: string;
  facility_type: 'sorting_plant' | 'transfer_station';
  capacity_t_per_day: number;
  operating_hours: string;
  manager_id: string;
  phone: string;
}

export const DEMO_FACILITIES: DemoFacility[] = [
  {
    id: ids.facility(1),
    code: 'RUH-SUL',
    name_en: 'Sulay Sorting Plant',
    name_ar: 'مصنع فرز السلي',
    city: 'Riyadh',
    city_ar: DEMO_CITY_AR.Riyadh,
    lat: 24.64,
    lng: 46.83,
    address: 'Al Sulay Industrial Area, Riyadh 14322',
    facility_type: 'sorting_plant',
    capacity_t_per_day: 120,
    operating_hours: '06:00-22:00',
    manager_id: ids.manager(1),
    phone: '+966112000301',
  },
  {
    id: ids.facility(2),
    code: 'JED-TRS',
    name_en: 'Jeddah Transfer Station',
    name_ar: 'محطة جدة للنقل',
    city: 'Jeddah',
    city_ar: DEMO_CITY_AR.Jeddah,
    lat: 21.5,
    lng: 39.23,
    address: 'Al Sulaimaniyah Industrial District, Jeddah 22524',
    facility_type: 'transfer_station',
    capacity_t_per_day: 60,
    operating_hours: '06:00-20:00',
    manager_id: ids.manager(2),
    phone: '+966122000302',
  },
];

export function facilityById(id: string): DemoFacility | undefined {
  return DEMO_FACILITIES.find((f) => f.id === id);
}

export function facilityForCity(city: DemoCity): DemoFacility {
  return city === 'Jeddah' ? DEMO_FACILITIES[1] : DEMO_FACILITIES[0];
}

// ── Zones ──────────────────────────────────────────────────────────────────

export interface DemoZone {
  id: string;
  name_en: string;
  name_ar: string;
  city: DemoCity;
  color: string;
  /** Closed ring in GeoJSON order: [lng, lat][], first point === last point. */
  polygon: Array<[number, number]>;
  center: { lat: number; lng: number };
}

function rect(west: number, south: number, east: number, north: number): Array<[number, number]> {
  return [
    [west, south],
    [east, south],
    [east, north],
    [west, north],
    [west, south],
  ];
}

export const DEMO_ZONES: DemoZone[] = [
  {
    id: ids.zone(1),
    name_en: 'Riyadh North',
    name_ar: 'شمال الرياض',
    city: 'Riyadh',
    color: '#2563EB',
    polygon: rect(46.55, 24.68, 46.7, 24.86),
    center: { lat: 24.77, lng: 46.625 },
  },
  {
    id: ids.zone(2),
    name_en: 'Riyadh East',
    name_ar: 'شرق الرياض',
    city: 'Riyadh',
    color: '#F4841A',
    polygon: rect(46.7, 24.63, 46.9, 24.84),
    center: { lat: 24.735, lng: 46.8 },
  },
  {
    id: ids.zone(3),
    name_en: 'Jeddah',
    name_ar: 'جدة',
    city: 'Jeddah',
    color: '#2CB259',
    polygon: rect(39.1, 21.4, 39.3, 21.7),
    center: { lat: 21.55, lng: 39.2 },
  },
];

// ── Collection points ──────────────────────────────────────────────────────

export interface DemoCollectionPoint {
  id: string;
  name_en: string;
  name_ar: string;
  supplier_id: string;
  zone_id: string;
  /** Set when the point is synced from an outlet collection site. */
  site_id: string | null;
  city: DemoCity;
  lat: number;
  lng: number;
  address: string;
  status: 'active';
}

const ZONE_BY_SITE: string[] = [ids.zone(1), ids.zone(1), ids.zone(2), ids.zone(2), ids.zone(3), ids.zone(3)];

const SYNCED_POINTS: DemoCollectionPoint[] = DEMO_SITES.map((site, i) => ({
  id: ids.collectionPoint(i + 1),
  name_en: `${DEMO_OUTLET_NAME} - ${site.name_en}`,
  name_ar: `${DEMO_OUTLET_NAME_AR} - ${site.name_ar}`,
  supplier_id: ids.supplier(1),
  zone_id: ZONE_BY_SITE[i],
  site_id: site.id,
  city: site.city,
  lat: site.lat,
  lng: site.lng,
  address: site.address,
  status: 'active',
}));

type PointRow = [string, string, number, number, number, string, string];

const OTHER_POINT_ROWS: PointRow[] = [
  // [name_en, name_ar, supplier n, lat, lng, zone id, address]
  ['Al Faisaliah Hotel - Loading Bay', 'فندق الفيصلية - منطقة التحميل', 2, 24.6903, 46.6851, ids.zone(1), 'King Fahd Rd, Al Olaya, Riyadh 12212'],
  ['King Fahd Medical City - Main Campus', 'مدينة الملك فهد الطبية - الحرم الرئيسي', 3, 24.6883, 46.7064, ids.zone(2), 'Al Sulimaniyah, Riyadh 12231'],
  ['King Fahd Medical City - Logistics Yard', 'مدينة الملك فهد الطبية - ساحة الخدمات', 3, 24.6835, 46.7121, ids.zone(2), 'Al Sulimaniyah, Riyadh 12231'],
  ['Granada Mall - Service Yard', 'غرناطة مول - ساحة الخدمات', 4, 24.7795, 46.7307, ids.zone(2), 'Eastern Ring Rd, Al Shuhada, Riyadh 13241'],
  ['Riyadh Schools District - Al Yasmin Complex', 'مدارس الرياض - مجمع الياسمين', 5, 24.825, 46.64, ids.zone(1), 'Al Yasmin, Riyadh 13325'],
  ['Riyadh Schools District - Al Sahafa Campus', 'مدارس الرياض - حرم الصحافة', 5, 24.8005, 46.6302, ids.zone(1), 'Al Sahafa, Riyadh 13315'],
];

export const DEMO_COLLECTION_POINTS: DemoCollectionPoint[] = [
  ...SYNCED_POINTS,
  ...OTHER_POINT_ROWS.map(([name_en, name_ar, supplierN, lat, lng, zone_id, address], i): DemoCollectionPoint => ({
    id: ids.collectionPoint(SYNCED_POINTS.length + i + 1),
    name_en,
    name_ar,
    supplier_id: ids.supplier(supplierN),
    zone_id,
    site_id: null,
    city: 'Riyadh',
    lat,
    lng,
    address,
    status: 'active',
  })),
];

export function collectionPointById(id: string): DemoCollectionPoint | undefined {
  return DEMO_COLLECTION_POINTS.find((p) => p.id === id);
}
